/**
 * BikeComparisonSummary — overall stats comparison table.
 */
import { Box, Typography, Table, TableBody, TableCell, TableHead, TableRow, useTheme } from '@mui/material';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';
const COLOR_B = '#f59e0b';

function fmtDur(s) {
  if (s == null) return '—';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m ${sec.toString().padStart(2, '0')}s`;
  return `${m}m ${sec.toString().padStart(2, '0')}s`;
}

function fmtDist(m) { return m == null ? '—' : `${(m / 1000).toFixed(2)} km`; }
function fmtSpd(v) { return v == null ? '—' : `${v.toFixed(1)} km/h`; }
function fmtPwr(v) { return (!v || v === 0) ? '—' : `${Math.round(v)} W`; }
function fmtElev(v) { return v == null ? '—' : `+${Math.round(v)} m`; }

function deltaLabel(va, vb, lowerBetter = false) {
  if (va == null || vb == null) return null;
  const diff = vb - va;
  if (Math.abs(diff) < 0.01) return null;
  const better = lowerBetter ? diff < 0 : diff > 0;
  return { diff, better };
}

function DeltaCell({ va, vb, fmt, lowerBetter = false }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const d = deltaLabel(va, vb, lowerBetter);
  return (
    <TableCell align="center" sx={{ borderColor: colors.grey[700] }}>
      {d ? (
        <Typography variant="body2" color={d.better ? '#22c55e' : '#ef4444'}>
          {d.diff > 0 ? '+' : ''}{fmt(d.diff)}
        </Typography>
      ) : (
        <Typography variant="body2" color={colors.grey[600]}>—</Typography>
      )}
    </TableCell>
  );
}

const STATS = [
  { label: 'Distance', keyA: 'total_distance', keyB: 'total_distance', fmt: fmtDist, lowerBetter: false },
  { label: 'Duration', keyA: 'total_duration', keyB: 'total_duration', fmt: fmtDur, lowerBetter: true },
  { label: 'Moving Time', keyA: 'total_moving_time', keyB: 'total_moving_time', fmt: fmtDur, lowerBetter: true },
  { label: 'Avg Speed', keyA: 'avg_speed', keyB: 'avg_speed', fmt: fmtSpd, lowerBetter: false },
  { label: 'Avg Power', keyA: 'avg_power', keyB: 'avg_power', fmt: fmtPwr, lowerBetter: false },
  { label: 'Elevation Gain', keyA: 'total_elevation_gain', keyB: 'total_elevation_gain', fmt: fmtElev, lowerBetter: false },
];

export default function BikeComparisonSummary({ analysisA, analysisB }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  if (!analysisA || !analysisB) return null;

  return (
    <Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ borderColor: colors.grey[700], color: colors.grey[400] }}>Metric</TableCell>
            <TableCell align="center" sx={{ borderColor: colors.grey[700], color: COLOR_A, fontWeight: 'bold' }}>A</TableCell>
            <TableCell align="center" sx={{ borderColor: colors.grey[700], color: COLOR_B, fontWeight: 'bold' }}>B</TableCell>
            <TableCell align="center" sx={{ borderColor: colors.grey[700], color: colors.grey[400] }}>B vs A</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {STATS.map(({ label, keyA, keyB, fmt, lowerBetter }) => {
            const va = analysisA[keyA];
            const vb = analysisB[keyB];
            return (
              <TableRow key={label}>
                <TableCell sx={{ borderColor: colors.grey[700], color: colors.grey[300] }}>{label}</TableCell>
                <TableCell align="center" sx={{ borderColor: colors.grey[700], color: COLOR_A }}>{fmt(va)}</TableCell>
                <TableCell align="center" sx={{ borderColor: colors.grey[700], color: COLOR_B }}>{fmt(vb)}</TableCell>
                <DeltaCell va={va} vb={vb} fmt={fmt} lowerBetter={lowerBetter} />
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
